import { useMutation } from 'react-query';
import { toast } from 'sonner';

import type { BundleIndexColumnId } from '../../../../backend/src/matter/subset/bundle/bundle-index-layout';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

export interface ExportBundleItem {
  schemaName: 'doc' | 'fact' | 'inconsistency' | 'entity';
  schemaId: string;
  customName?: string;
  dateText?: string;
}

export interface ExportBundleParams {
  matterId: string;
  subsetId: string;
  name: string;
  config?: {
    subheading?: string;
    pageNumberPosition?: { x: number; y: number };
    fontFamily?: 'times' | 'helvetica' | 'courier';
    fontSize?: number;
    pageNumberSize?: number;
    textColor?: 'black' | 'gray' | 'blue' | 'red';
    pageNumberBold?: boolean;
    pageNumberItalic?: boolean;
    pageNumberUnderline?: boolean;
    numberIndexPages?: boolean;
    useSmallRomanIndexNumbering?: boolean;
    continuePageNumberingAfterIndex?: boolean;
    repeatIndexPageTitle?: boolean;
    columns?: BundleIndexColumnId[];
  };
  items: ExportBundleItem[];
}

interface ExportResponse {
  exportId: string;
  jobId: string;
}

const useExportBundle = () => {
  const { getFetchConfig } = useGetFetchConfig();

  const exportMutation = useMutation(
    async ({ matterId, subsetId, ...body }: ExportBundleParams): Promise<ExportResponse> => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: body,
      });

      const endpoint = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/bundle/export`;
      const response = await fetch(endpoint, fetchConfig);

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(errorBody?.message ?? 'Failed to start bundle export');
      }

      return response.json();
    },
    {
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to export bundle. Please try again.');
      },
    },
  );

  const bundleEndpoints = (matterId: string, subsetId: string) => {
    const base = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/bundle`;
    return {
      progressEndpoint: () => `${base}/export-progress`,
      statusEndpoint: () => `${base}/export-status`,
    };
  };

  return { exportMutation, bundleEndpoints };
};

export default useExportBundle;
